// useToast Hook
// Global toast notification system using Zustand for state management
// Provides a simple API: toast.success('message'), toast.error('message'), etc.

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

interface ToastStore {
  toasts: Toast[];
  /** Bumps on every polite (non-error) emission, so a repeat still mutates the live region. */
  politeSeq: number;
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

// Generate unique IDs for toasts
let toastId = 0;
const generateId = () => `toast-${++toastId}-${Date.now()}`;

// A flapping connection with four friends stacked toasts until they buried the
// Submit button (#409). Three is what a phone shows without covering the CTA.
const MAX_TOASTS = 3;

// Zustand store for global toast state
export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],
  politeSeq: 0,

  addToast: (toast) => {
    const id = generateId();
    const politeSeq = get().politeSeq + (toast.type === 'error' ? 0 : 1);

    // A repeat of what's already on top doesn't stack — it replaces it, so the
    // timer restarts. Returning the old id instead left a second tap with
    // whatever was left of the first toast's 5s, or nothing at all when the
    // repeat landed inside its 200ms exit animation.
    const last = get().toasts.at(-1);
    if (last && last.type === toast.type && last.message === toast.message) {
      set((state) => ({ toasts: [...state.toasts.slice(0, -1), { ...toast, id }], politeSeq }));
      return id;
    }

    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }].slice(-MAX_TOASTS),
      politeSeq,
    }));
    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [] });
  },
}));

// Default durations by type
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

const emit =
  (type: ToastType) =>
  (message: string, options?: { duration?: number }): string =>
    useToastStore.getState().addToast({
      type,
      message,
      duration: options?.duration ?? DEFAULT_DURATIONS[type],
    });

// A singleton, usable outside React components (e.g. in socket handlers)
export const toast = {
  success: emit('success'),
  error: emit('error'),
  warning: emit('warning'),
  info: emit('info'),
};
